import type {
  FilterConfig,
  FilterFieldConfig,
  FilterOption,
  UserFilterConfig,
} from '@/utilities/types/shared-filter'
import type {
  TicketData,
  AreaData,
  UserData,
  ProvinceData,
  RoleData,
  AreaControllerData,
  SupervisorData,
} from '@/utilities/types/table'
import { createDefaultFilterState } from '@/utilities/types/shared-filter'
import { PROCESSING_STATUSES, PROCESSED_STATUSES } from './ticketStatus'

const forProcessingConfig: FilterConfig<TicketData> = {
  fields: [
    {
      type: 'select',
      field: 'status',
      label: 'Status',
      options: PROCESSING_STATUSES.map((status) => ({
        value: status,
        label: status,
      })),
    },
    {
      type: 'select',
      field: 'type',
      label: 'Type of Concern',
      options: [
        { value: 'Building', label: 'Building' },
        { value: 'Interior', label: 'Interior' },
        { value: 'Appliance', label: 'Appliance' },
        { value: 'Collateral', label: 'Collateral' },
      ],
      isMulti: true,
    },
    {
      type: 'select',
      field: 'method',
      label: 'Method of Repair',
      options: [
        {
          value: 'Request C/O Dealer 50-50',
          label: 'Request C/O Dealer 50-50',
        },
        { value: 'Request C/O YMPH 100%', label: 'Request C/O YMPH 100%' },
        {
          value: 'Reimbursement C/O Dealer 50-50',
          label: 'Reimbursement C/O Dealer 50-50',
        },
        { value: 'Request C/O YMPH 50-50', label: 'Request C/O YMPH 50-50' },
      ],
      isMulti: true,
    },
    {
      type: 'date',
      field: 'statusDate',
      label: 'Status Date',
    },
    {
      type: 'date',
      field: 'modifiedDate',
      label: 'Modified Date',
    },
  ],
  sortableFields: ['id', 'purpose', 'type', 'status', 'method'],
  defaultState: createDefaultFilterState<TicketData>(),
}

const processedConfig: FilterConfig<TicketData> = {
  fields: [
    {
      type: 'select',
      field: 'status',
      label: 'Status',
      options: PROCESSED_STATUSES.map((status) => ({
        value: status,
        label: status,
      })),
    },
    {
      type: 'select',
      field: 'type',
      label: 'Type of Concern',
      options: [
        { value: 'Building', label: 'Building' },
        { value: 'Interior', label: 'Interior' },
        { value: 'Appliance', label: 'Appliance' },
        { value: 'Collateral', label: 'Collateral' },
      ],
      isMulti: true,
    },
    {
      type: 'select',
      field: 'method',
      label: 'Method of Repair',
      options: [
        {
          value: 'Request C/O Dealer 50-50',
          label: 'Request C/O Dealer 50-50',
        },
        { value: 'Request C/O YMPH 100%', label: 'Request C/O YMPH 100%' },
        {
          value: 'Reimbursement C/O Dealer 50-50',
          label: 'Reimbursement C/O Dealer 50-50',
        },
        { value: 'Request C/O YMPH 50-50', label: 'Request C/O YMPH 50-50' },
      ],
      isMulti: true,
    },
    {
      type: 'date',
      field: 'statusDate',
      label: 'Status Date',
    },
    {
      type: 'date',
      field: 'modifiedDate',
      label: 'Modified Date',
    },
  ],
  sortableFields: ['id', 'purpose', 'type', 'status', 'method'],
  defaultState: createDefaultFilterState<TicketData>(),
}

const bmProcessedConfig: FilterConfig<TicketData> = {
  fields: [
    {
      type: 'select',
      field: 'status',
      label: 'Status',
      options: PROCESSED_STATUSES.map((status) => ({
        value: status,
        label: status,
      })),
    },
    {
      type: 'select',
      field: 'type',
      label: 'Type of Concern',
      options: [
        { value: 'Building', label: 'Building' },
        { value: 'Interior', label: 'Interior' },
        { value: 'Appliance', label: 'Appliance' },
        { value: 'Collateral', label: 'Collateral' },
      ],
      isMulti: true,
    },
    {
      type: 'select',
      field: 'method',
      label: 'Method of Repair',
      options: [
        {
          value: 'Request C/O Dealer 50-50',
          label: 'Request C/O Dealer 50-50',
        },
        { value: 'Request C/O YMPH 100%', label: 'Request C/O YMPH 100%' },
        {
          value: 'Reimbursement C/O Dealer 50-50',
          label: 'Reimbursement C/O Dealer 50-50',
        },
        { value: 'Request C/O YMPH 50-50', label: 'Request C/O YMPH 50-50' },
      ],
      isMulti: true,
    },
    {
      type: 'date',
      field: 'statusDate',
      label: 'Status Date',
    },
    {
      type: 'date',
      field: 'modifiedDate',
      label: 'Modified Date',
    },
  ],
  sortableFields: ['id', 'purpose', 'type', 'status', 'method'],
  defaultState: createDefaultFilterState<TicketData>(),
}

export const maintenanceTicketFilterConfig = {
  forProcessing: forProcessingConfig,
  processed: processedConfig,
  bmProcessed: bmProcessedConfig,
}

export const userManagementFilterConfig = (
  roles: RoleData[] = [],
): UserFilterConfig<UserData> => {
  const uniqueRoles = Array.from(new Set(roles.map((role) => role.name)))
    .filter(Boolean) 
    .map((role) => ({ value: role, label: role }))

  return {
    fields: [
      {
        type: 'date',
        field: 'createdDate',
        label: 'Created Date',
      },
      {
        type: 'date',
        field: 'modifiedDate',
        label: 'Modified Date',
      },
      {
        type: 'select',
        field: 'roleName',
        label: 'Role',
        options: uniqueRoles ?? [],
        isMulti: false,
      },
    ],
    sortableFields: ['id', 'name', 'createdDate', 'modifiedDate'],
    defaultState: createDefaultFilterState<UserData>(),
  }
}

export const ticketsMaintenanceFilterConfig =
  (): FilterConfig<TicketData> => {
    return {
      fields: [
        {
          type: 'date',
          field: 'createdDate',
          label: 'Created Date',
        },
        {
          type: 'date',
          field: 'modifiedDate',
          label: 'Modified Date',
        },
        {
          type: 'select',
          field: 'status',
          label: 'Status',
          options: ['FOR REVISION', 'FOR APPROVAL', 'REJECTED', 'CLOSED', 'FOR COC', 'FOR CONTRACTOR QUOTATION', 'FOR PO QUOTATION', 'FOR COC DATE COMPLETION'],
          isMulti: false,
        },
        {
          type: 'select',
          field: 'methodOfRepair',
          label: 'Method of Repair',
          options: ['C/O Dealer', 'C/O YMPH'],
          isMulti: false,
        },
        {
          type: 'select',
          field: 'typeOfConcern',
          label: 'Type of Concern',
          options: ['Building', 'Interior', 'Exterior', 'Appliance', 'Collateral', 'Others'],
          isMulti: false,
        },
      ],
      sortableFields: ['id', 'name', 'createdDate', 'modifiedDate'],
      defaultState: createDefaultFilterState<TicketData>(),
    }
  }

export const createProvinceManagementFilterConfig =
  (): FilterConfig<ProvinceData> => {
    return {
      fields: [
        {
          type: 'date',
          field: 'createdDate',
          label: 'Created Date',
        },
        {
          type: 'date',
          field: 'modifiedDate',
          label: 'Modified Date',
        },
      ],
      sortableFields: [
        'id',
        'name',
        'regionName',
        'createdDate',
        'modifiedDate',
      ],
      defaultState: createDefaultFilterState<ProvinceData>(),
    }
  }

export const areaManagementFilterConfig = (): FilterConfig<AreaData> => {
  return {
    fields: [
      {
        type: 'date',
        field: 'createdDate',
        label: 'Created Date',
      },
      {
        type: 'date',
        field: 'modifiedDate',
        label: 'Modified Date',
      },
    ],
    sortableFields: ['id', 'name', 'createdDate', 'modifiedDate'],
    defaultState: createDefaultFilterState(),
  }
}

export const supervisorFilterConfig = (): FilterConfig<SupervisorData> => {
  return {
    fields: [
      {
        type: 'date',
        field: 'createdDate',
        label: 'Created Date',
      },
      {
        type: 'date',
        field: 'modifiedDate',
        label: 'Modified Date',
      },
    ],
    sortableFields: ['id', 'name', 'createdDate', 'modifiedDate'],
    defaultState: createDefaultFilterState(),
  }
}

export const areaControllerFilterConfig =
  (): FilterConfig<AreaControllerData> => {
    return {
      fields: [
        {
          type: 'date',
          field: 'createdDate',
          label: 'Created Date',
        },
        {
          type: 'date',
          field: 'modifiedDate',
          label: 'Modified Date',
        },
      ],
      sortableFields: ['id', 'name', 'createdDate', 'modifiedDate'],
      defaultState: createDefaultFilterState(),
    }
  }
